import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, MapPin, Clock, Check } from "lucide-react";
import { api, pickLang } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import VideoEmbed from "../components/VideoEmbed";
import ExperiencePlaceholder from "../components/ExperiencePlaceholder";

export default function ExperienceDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [exp, setExp] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get(`/experiences/${slug}`)
      .then((r) => setExp(r.data))
      .catch(() => nav("/experiencias"));
  }, [slug, nav]);

  if (!exp) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bn-bg)" }}>
        <div className="bn-eyebrow">{t("common.loading")}</div>
      </div>
    );
  }

  const images = exp.images?.length ? exp.images : [];

  return (
    <div data-testid="experience-detail">
      <Navbar />

      {/* Hero gallery */}
      <section className="pt-24 md:pt-28">
        <div className="bn-container px-4 md:px-8">
          <div className="grid md:grid-cols-12 gap-4 md:gap-6">
            <div className="md:col-span-8 relative aspect-[4/3] md:aspect-[16/10] overflow-hidden" style={{ borderRadius: "var(--bn-card-radius)" }}>
              {images.length === 0 ? (
                <ExperiencePlaceholder />
              ) : (
                <motion.img
                  key={idx}
                  src={images[idx]}
                  alt={exp.name}
                  initial={{ opacity: 0.4 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="w-full h-full object-cover"
                />
              )}
              {images.length > 1 && (
                <>
                  <button onClick={() => setIdx((idx - 1 + images.length) % images.length)} className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bn-glass flex items-center justify-center" data-testid="carousel-prev" aria-label="anterior">
                    <ChevronLeft size={18} />
                  </button>
                  <button onClick={() => setIdx((idx + 1) % images.length)} className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bn-glass flex items-center justify-center" data-testid="carousel-next" aria-label="siguiente">
                    <ChevronRight size={18} />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setIdx(i)}
                        className="transition-all"
                        style={{
                          width: i === idx ? 22 : 8,
                          height: 4,
                          borderRadius: 10,
                          background: i === idx ? "var(--bn-accent)" : "rgba(255,255,255,0.6)",
                        }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-4">
              {images.slice(0, 3).map((src, i) => (
                <button key={i} onClick={() => setIdx(i)} className="relative overflow-hidden aspect-[4/3]" style={{ borderRadius: "var(--bn-card-radius)", border: i === idx ? "2px solid var(--bn-accent)" : "1px solid var(--bn-border)" }}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bn-section pb-10">
        <div className="bn-container px-6 md:px-8">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-7">
              <div className="bn-eyebrow" style={{ color: "var(--bn-accent)" }}>{pickLang(exp, "category", lang)}</div>
              <h1 className="bn-heading text-4xl md:text-6xl mt-3 leading-[1.05]" data-testid="exp-title">{pickLang(exp, "name", lang)}</h1>
              <div className="flex items-center gap-5 mt-5 text-sm" style={{ color: "var(--bn-text-muted)" }}>
                {pickLang(exp, "duration", lang) && <div className="flex items-center gap-1.5"><Clock size={14} /> {pickLang(exp, "duration", lang)}</div>}
                {pickLang(exp, "location", lang) && <div className="flex items-center gap-1.5"><MapPin size={14} /> {pickLang(exp, "location", lang)}</div>}
              </div>
              <p className="mt-8 text-base md:text-lg leading-relaxed" style={{ color: "var(--bn-text)" }}>
                {pickLang(exp, "description", lang) || pickLang(exp, "short_description", lang)}
              </p>

              {pickLang(exp, "includes", lang)?.length > 0 && (
                <div className="mt-10">
                  <div className="bn-eyebrow mb-5">{t("detail.includes")}</div>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {pickLang(exp, "includes", lang).map((inc, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: "var(--bn-accent)", color: "var(--bn-accent-fg)" }}>
                          <Check size={14} />
                        </div>
                        <span className="bn-body-font">{inc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {exp.video_url && (
                <div className="mt-12">
                  <div className="bn-eyebrow mb-5">{t("detail.video")}</div>
                  <VideoEmbed url={exp.video_url} title={pickLang(exp, "name", lang)} />
                </div>
              )}

              {exp.maps_url && (
                <div className="mt-10">
                  <a href={exp.maps_url} target="_blank" rel="noreferrer" className="bn-btn-ghost inline-flex items-center gap-2" data-testid="maps-link">
                    <MapPin size={14} /> {t("detail.maps")}
                  </a>
                </div>
              )}
            </div>

            {/* Booking widget - simplified: price + CTA only */}
            <div className="md:col-span-5">
              <div className="bn-card p-6 md:p-8 md:sticky md:top-28" data-testid="booking-widget">
                <div className="bn-eyebrow" style={{ color: "var(--bn-text-muted)" }}>
                  {lang === "es" ? "Desde" : "From"}
                </div>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="bn-heading text-5xl md:text-6xl">${exp.price}</span>
                  <span className="bn-eyebrow" style={{ color: "var(--bn-text-muted)" }}>{t("detail.per_person")} · {exp.currency}</span>
                </div>

                <button onClick={() => nav(`/reservar/${exp.id}`)} className="bn-btn-primary w-full mt-6 justify-center" data-testid="book-now-btn">
                  {t("detail.book_now")}
                </button>

                <p className="mt-4 text-xs text-center" style={{ color: "var(--bn-text-muted)" }}>
                  {t("detail.pay_safe")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
